import type { FilterCondition } from '@moment/trading-types/filter-expression'
import type {
  MoodysRating,
  SPRating,
} from '@moment/trading-types/reference-data'
import type { FilterListsMap } from '@/hooks/use-get-filter-lists-map'
import type { ConstraintInstanceType } from '@/portfolio/shared/constraint/types'
import type { Holding } from '.'

import { toError } from '@moment/observability/logger'
import { isNil } from 'lodash'

import { HorizontalTargetBarChart } from '@/components/atomic/horizontal-target-bar-chart'
import { IconName } from '@/components/atomic/icon'
import { TargetBarChart } from '@/components/atomic/target-bar-chart'
import { TargetNumberLine } from '@/components/molecules/target-number-line'
import { createLogger } from '@/lib/logger'
import { isCompliant } from '@/portfolio/shared/compliance/is-compliant'
import { renderEqualWeightLabel } from '@/portfolio/shared/constraint/equal-weight'
import {
  getFormattedFieldName,
  renderCashflowBandRange,
  renderEqualWeightTolerance,
} from '@/portfolio/shared/constraint/utils'
import {
  type AllocationConstraintValidation,
  type AverageValueConstraintValidation,
  type CashflowBandConstraintValidation,
  ComplianceStatus,
  type DiversificationConstraintValidation,
  type EqualWeightConstraintValidation,
  type EqualWeightDeviation,
  isEqualWeightValidation,
  type LadderPolicyConstraintValidation,
  ModelConstraintType,
  type ModelConstraintValidation,
  type Piece,
  type UniverseConstraintValidation,
} from '@/types/api/constraints'
import { ValueUnit } from '@/types/enums/value-unit'
import { getMaturityOptionLabel } from '@/utils/constraints/maturity-option'
import {
  decimalToPercentage,
  formatMoney,
  formatNumber,
  formatPercentage,
  formatPrice,
} from '@/utils/presentation/formatting'
import {
  isMoodysRatingField,
  isSpRatingField,
} from '@/utils/validators/constraints/average-value'

const LOGGER = createLogger('components/molecules/compliance-list/helpers')

import {
  ChartContainer,
  TooltipColorIndicator,
  TooltipContainer,
  TooltipItemContainer,
  TooltipItemText,
  TooltipLabel,
} from './styles'

// Types
interface ViolationDetail {
  isin: string
  description: string
  weight: number
  note?: string
}

interface ConstraintItemData {
  label: string
  value: string | React.ReactNode
  status: ComplianceStatus
  violationsCount?: number
  violations?: ViolationDetail[]
  isMarketValueConstraint?: boolean
}

interface ConstraintConfig {
  titleText: string
  icon: IconName
  iconColor: string
  iconBackgroundColor: string
  type: string
}

interface MaturityDateRange {
  greater_than_or_equal_to: string
  less_than: string
}

interface SectorWeight {
  name: string
  fullName: string
  value: number
}

// Shared utility functions
function getComplianceStatus(isCompliant: boolean): ComplianceStatus {
  return isCompliant ? ComplianceStatus.Satisfied : ComplianceStatus.Violation
}

function createViolationDetails(
  isins: string[],
  holdings?: Holding[],
): ViolationDetail[] {
  if (!holdings) {
    return isins.map((isin) => ({
      isin,
      description: '',
      weight: 0,
    }))
  }

  const holdingsMap = new Map(
    holdings.map((holding) => [holding.isin, holding]),
  )

  return isins.map((isin) => {
    const holding = holdingsMap.get(isin)
    return {
      isin,
      description: holding?.description || '',
      weight: decimalToPercentage(holding?.weight || 0),
    }
  })
}

function formatDateRange(startDate: Date, endDate: Date): string {
  return `${startDate.toLocaleDateString()} to ${endDate.toLocaleDateString()}`
}

// Helper for creating constraint items with consistent structure
function createConstraintItem(
  label: string,
  value: string | React.ReactNode,
  isCompliant: boolean,
  violations?: ViolationDetail[],
  violationsCount?: number,
): ConstraintItemData {
  return {
    label,
    value,
    status: getComplianceStatus(isCompliant),
    violations,
    violationsCount,
  }
}

// Constants
export const CONSTRAINT_CONFIGS: ConstraintConfig[] = [
  {
    titleText: 'Ladder policy',
    icon: IconName.ChartBarSquareIcon,
    iconColor: 'text-moment-purple-700 dark:text-moment-purple-200',
    iconBackgroundColor: 'bg-moment-purple-100 dark:bg-moment-purple-900',
    type: 'ladder_policy',
  },
  {
    titleText: 'Universe',
    icon: IconName.CircleStackIcon,
    iconColor: 'text-moment-green-800 dark:text-moment-green-600',
    iconBackgroundColor: 'bg-moment-green-100 dark:bg-moment-green-1000',
    type: 'universe',
  },
  {
    titleText: 'Target averages',
    icon: IconName.ViewfinderCircleIcon,
    iconColor: 'text-moment-blue-700 dark:text-moment-blue-400',
    iconBackgroundColor: 'bg-moment-blue-100 dark:bg-moment-blue-900',
    type: 'average_value',
  },
  {
    titleText: 'Allocation',
    icon: IconName.ShareIcon,
    iconColor: 'text-moment-orange-700 dark:text-moment-orange-400',
    iconBackgroundColor: 'bg-moment-orange-100 dark:bg-moment-orange-1000',
    type: 'allocation',
  },
  {
    titleText: 'Diversification',
    icon: IconName.SwatchIcon,
    iconColor: 'text-moment-red-700 dark:text-moment-red-200',
    iconBackgroundColor: 'bg-moment-red-100 dark:bg-moment-red-900',
    type: 'diversification',
  },
  {
    titleText: 'Buy universe',
    icon: IconName.CircleStackIcon,
    iconColor: 'text-moment-blue-grey-800 dark:text-moment-blue-grey-600',
    iconBackgroundColor:
      'bg-moment-blue-grey-100 dark:bg-moment-blue-grey-1000',
    type: 'buy_universe',
  },
  {
    titleText: 'Cash flow',
    icon: IconName.ArrowsUpDownIcon,
    iconColor: 'text-moment-green-800 dark:text-moment-green-600',
    iconBackgroundColor: 'bg-moment-green-100 dark:bg-moment-green-1000',
    type: 'cashflow_band',
  },
]

// Helper functions
const formatFieldLabel = (field: string): string => {
  return field
    .replace(/_/g, ' ')
    .split(' ')
    .map((word: string, index: number) =>
      index === 0
        ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        : word.toLowerCase(),
    )
    .join(' ')
}

// Helper function for sentence casing values. Takes `unknown` because filter
// values are only nominally strings — a numeric or boolean field's `in` list
// arrives as numbers/booleans, which used to throw on `charAt`.
function toSentenceCase(value: unknown): string {
  const text = String(value)
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
}

// Ladder Policy Helper Functions
function getLadderPolicyBaseItems(
  constraint: LadderPolicyConstraintValidation,
): ConstraintItemData[] {
  return [
    createConstraintItem(
      'Maturity option',
      getMaturityOptionLabel(
        constraint.constraint.maturity_option,
        constraint.constraint.use_call_adjusted_maturity,
      ),
      true,
    ),
    createConstraintItem(
      'Period length',
      `${constraint.constraint.period_length} months`,
      constraint.constraint.period_length > 0,
    ),
    getLadderToleranceItem(constraint.constraint),
  ]
}

function getLadderToleranceItem(
  ladder: LadderPolicyConstraintValidation['constraint'],
): ConstraintItemData {
  const { weight_tolerance, market_value_tolerance } = ladder
  if (weight_tolerance == null && market_value_tolerance != null) {
    return createConstraintItem(
      'Bucket market value tolerance',
      formatMoney(market_value_tolerance, { numDigits: 0 }),
      market_value_tolerance > 0,
    )
  }
  return createConstraintItem(
    'Bucket weight tolerance',
    formatPercentage(decimalToPercentage(weight_tolerance ?? 0), {
      numDigits: 2,
    }),
    (weight_tolerance ?? 0) > 0,
  )
}

/** Rung tolerance in percent, converting a dollar tolerance via the rungs' own value/weight. */
function getLadderTolerancePercent(
  constraint: LadderPolicyConstraintValidation,
): number {
  const { weight_tolerance, market_value_tolerance } = constraint.constraint
  if (weight_tolerance != null) return weight_tolerance * 100
  if (market_value_tolerance == null) return 0
  const rung = constraint.additional_constraints?.find(
    (c): c is AllocationConstraintValidation =>
      c.constraint.type === ModelConstraintType.Allocation &&
      ((c as AllocationConstraintValidation).weight ?? 0) > 0 &&
      ((c as AllocationConstraintValidation).market_value ?? 0) > 0,
  )
  if (!rung?.weight || !rung.market_value) return 0
  return (market_value_tolerance / (rung.market_value / rung.weight)) * 100
}

function getLadderPolicyDateRange(
  constraint: LadderPolicyConstraintValidation,
  maturityRanges: MaturityDateRange[],
  holdings?: Holding[],
): ConstraintItemData | null {
  if (!maturityRanges.length) return null

  const startDates = maturityRanges.map((range) =>
    new Date(range.greater_than_or_equal_to).getTime(),
  )
  const endDates = maturityRanges.map((range) =>
    new Date(range.less_than).getTime(),
  )
  const earliestDate = new Date(Math.min(...startDates))
  const latestDate = new Date(Math.max(...endDates))

  const violationsCount =
    constraint.additional_constraints?.filter(
      (c) =>
        !c.compliant && c.constraint.type === ModelConstraintType.Allocation,
    ).length || 0

  const violationIsins = constraint.additional_constraints
    ?.filter(
      (c) =>
        !c.compliant && c.constraint.type === ModelConstraintType.Allocation,
    )
    .flatMap(
      (c) => (c as AllocationConstraintValidation).holdings_in_allocation || [],
    )

  return createConstraintItem(
    'Date range',
    formatDateRange(earliestDate, latestDate),
    constraint.compliant,
    createViolationDetails(violationIsins || [], holdings),
    violationsCount,
  )
}

function getLadderPolicyAllocation(
  constraint: LadderPolicyConstraintValidation,
): ConstraintItemData | null {
  const bucketData = constraint.additional_constraints
    ?.filter((item) => item.constraint.type === ModelConstraintType.Allocation)
    .map((allocationConstraint, index) => {
      const { maturity_date } = allocationConstraint.constraint
        .filter as FilterCondition
      if (!maturity_date) return null

      const { weight = 0 } =
        allocationConstraint as AllocationConstraintValidation
      const startPeriod = constraint.constraint.start_period || 0
      const periodNumber = startPeriod + index
      const bucketType =
        constraint.constraint.period_length % 12 === 0 ? 'Year' : 'Month'
      const startRange = periodNumber * constraint.constraint.period_length
      const endRange = (periodNumber + 1) * constraint.constraint.period_length

      return {
        period:
          bucketType === 'Year'
            ? `${startRange / 12} - ${endRange / 12}`
            : `${startRange} - ${endRange}`,
        weight: decimalToPercentage(weight || 0),
      }
    })
    .filter((item): item is NonNullable<typeof item> => item !== null)

  if (!bucketData?.length) return null

  const targetValue =
    100 /
    ((constraint.constraint.end_period || 1) -
      (constraint.constraint.start_period || 0))
  const tolerance = getLadderTolerancePercent(constraint)
  const xAxisLabel =
    constraint.constraint.period_length % 12 === 0 ? 'Years' : 'Months'

  return createConstraintItem(
    'Ladder Allocation',
    <div className="h-64 w-full border border-primary-border rounded-[6px]">
      <TargetBarChart
        data={bucketData}
        xAxisKey="period"
        bars={[
          {
            key: 'weight',
            fill: '#5A7B7D',
            radius: [4, 4, 0, 0],
            violationColor: '#F07070',
          },
        ]}
        tooltipLabelPrefix={xAxisLabel}
        xAxisLabel={xAxisLabel}
        barSize={16}
        targetValue={targetValue}
        minValue={targetValue - tolerance}
        maxValue={targetValue + tolerance}
      />
    </div>,
    constraint.compliant,
  )
}

function getLadderPolicyItems(
  constraint: LadderPolicyConstraintValidation,
  holdings?: Holding[],
): ConstraintItemData[] {
  const items = getLadderPolicyBaseItems(constraint)

  if (constraint.additional_constraints?.length) {
    const maturityRanges = constraint.additional_constraints
      .map((allocation) => {
        const filter = allocation.constraint.filter as FilterCondition
        return filter.maturity_date
      })
      .filter(
        (range): range is MaturityDateRange =>
          range !== undefined &&
          typeof range.greater_than_or_equal_to === 'string' &&
          typeof range.less_than === 'string',
      )

    const dateRangeItem = getLadderPolicyDateRange(
      constraint,
      maturityRanges,
      holdings,
    )
    if (dateRangeItem) items.push(dateRangeItem)

    const allocationItem = getLadderPolicyAllocation(constraint)
    if (allocationItem) items.push(allocationItem)
  }

  return items
}

// Universe constraint helper functions
function handleInListFilter(
  key: string,
  value: { in: readonly unknown[] },
  constraint: UniverseConstraintValidation,
  violations?: ViolationDetail[],
): ConstraintItemData {
  const hasManyItems = value.in.length > 10

  let displayValue: string
  if (hasManyItems) {
    displayValue = `${value.in.length} items`
  } else {
    // Show all items as before
    displayValue = value.in.map((val) => toSentenceCase(val)).join(', ')
  }

  return createConstraintItem(
    key,
    displayValue,
    constraint.compliant,
    violations,
  )
}

function handleNotInListFilter(
  key: string,
  value: { not_in: readonly unknown[] },
  constraint: UniverseConstraintValidation,
  violations?: ViolationDetail[],
): ConstraintItemData {
  const hasManyItems = value.not_in.length > 10

  let displayValue: string
  if (hasManyItems) {
    displayValue = `≠ ${value.not_in.length} items`
  } else {
    // Show all items with NOT IN prefix
    displayValue = `≠ ${value.not_in.map((val) => toSentenceCase(val)).join(', ')}`
  }

  return createConstraintItem(
    key,
    displayValue,
    constraint.compliant,
    violations,
  )
}

function handleEqualToFilter(
  key: string,
  value: { equal_to: string | number },
  constraint: UniverseConstraintValidation,
  violations?: ViolationDetail[],
): ConstraintItemData {
  let displayValue =
    typeof value.equal_to === 'string'
      ? toSentenceCase(value.equal_to.toString())
      : value.equal_to.toString()

  // Convert 'Cd' to 'CD' if present
  if (displayValue === 'Cd') {
    displayValue = 'CD'
  }

  return createConstraintItem(
    key,
    `= ${displayValue}`,
    constraint.compliant,
    violations,
  )
}

function handleNotEqualToFilter(
  key: string,
  value: { not_equal_to: string | number },
  constraint: UniverseConstraintValidation,
  violations?: ViolationDetail[],
): ConstraintItemData {
  let displayValue =
    typeof value.not_equal_to === 'string'
      ? toSentenceCase(value.not_equal_to.toString())
      : value.not_equal_to.toString()

  // Convert 'Cd' to 'CD' if present
  if (displayValue === 'Cd') {
    displayValue = 'CD'
  }

  return createConstraintItem(
    key,
    `≠ ${displayValue}`,
    constraint.compliant,
    violations,
  )
}

function handleRangeFilter(
  key: string,
  value: {
    less_than_or_equal_to?: number | string | null
    greater_than_or_equal_to?: number | string | null
    less_than?: number | string | null
    greater_than?: number | string | null
  },
  constraint: UniverseConstraintValidation,
  violations?: ViolationDetail[],
): ConstraintItemData[] {
  const items: ConstraintItemData[] = []

  // Special handling for S&P rating range
  if (value.greater_than_or_equal_to && value.less_than_or_equal_to) {
    items.push(
      createConstraintItem(
        key,
        `= ${value.greater_than_or_equal_to} to ${value.less_than_or_equal_to}`,
        constraint.compliant,
        violations,
      ),
    )
    return items
  }

  // Helper function to safely format value based on field type
  const formatValue = (val: number | string | null | undefined): string => {
    if (val === null || val === undefined) return ''
    const numericValue = typeof val === 'string' ? parseFloat(val) : val
    // Format based on field type
    if (key === 'close_ytw' || key === 'coupon') {
      return formatPercentage(numericValue, { numDigits: 1 })
    }
    if (key === 'close_price') {
      return formatPrice(numericValue)
    }
    if (key === 'issue_size') {
      return formatNumber(numericValue)
    }

    return val.toString()
  }

  if (
    value.less_than_or_equal_to !== null &&
    value.less_than_or_equal_to !== undefined
  ) {
    items.push(
      createConstraintItem(
        `Max ${key}`,
        `≤ ${formatValue(value.less_than_or_equal_to)}`,
        constraint.compliant,
        violations,
      ),
    )
  }

  if (
    value.greater_than_or_equal_to !== null &&
    value.greater_than_or_equal_to !== undefined
  ) {
    items.push(
      createConstraintItem(
        `Min ${key}`,
        `≥ ${formatValue(value.greater_than_or_equal_to)}`,
        constraint.compliant,
        violations,
      ),
    )
  }

  if (value.greater_than_or_equal_to && value.less_than) {
    try {
      const startDate = new Date(value.greater_than_or_equal_to)
      const endDate = new Date(value.less_than)

      // Check if dates are valid before formatting
      // biome-ignore lint/suspicious/noGlobalIsNan: Bulk suppression as part of biome migration
      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
        items.push(
          createConstraintItem(
            key,
            formatDateRange(startDate, endDate),
            constraint.compliant,
            violations,
          ),
        )
      }
    } catch (error) {
      LOGGER.error('Error formatting date range', { error: toError(error) })
    }
  }

  if (value.greater_than) {
    items.push(
      createConstraintItem(
        `Min ${key}`,
        `> ${formatValue(value.greater_than)}`,
        constraint.compliant,
        violations,
      ),
    )
  }

  if (value.less_than) {
    items.push(
      createConstraintItem(
        `Max ${key}`,
        `< ${formatValue(value.less_than)}`,
        constraint.compliant,
        violations,
      ),
    )
  }

  return items
}

function getUniverseItems(
  constraint: UniverseConstraintValidation,
  holdings?: Holding[],
  filterListsMap?: FilterListsMap,
  constraintInstanceType?: ConstraintInstanceType,
): ConstraintItemData[] {
  const items: ConstraintItemData[] = []
  const filters = constraint.constraint.filter

  // Calculate violations count
  const violations = constraint.holdings
    ?.filter((holding) => !holding.compliant)
    ?.map((holding) => {
      const holdingData = holdings?.find(
        (h) => h.isin === holding.instrument_id,
      )
      return {
        isin: holding.instrument_id,
        description: holdingData?.description || '',
        weight: holding.weight * 100,
      }
    })
  const violationsCount = violations?.length || 0

  Object.entries(filters).forEach(([key, value]) => {
    const fieldLabel = getFormattedFieldName(key, constraintInstanceType)
    if (value.in) {
      items.push(handleInListFilter(fieldLabel, value, constraint, violations))
    }
    if (value.not_in) {
      items.push(
        handleNotInListFilter(fieldLabel, value, constraint, violations),
      )
    }
    if (value.in_filter_list) {
      // Resolve filter list name from the data
      const filterList = filterListsMap?.[value.in_filter_list]
      const listName = filterList?.name || value.in_filter_list

      items.push(
        createConstraintItem(
          fieldLabel,
          `= ${listName}`,
          constraint.compliant,
          violations,
        ),
      )
    }
    if (value.not_in_filter_list) {
      // Resolve filter list name from the data
      const filterList = filterListsMap?.[value.not_in_filter_list]
      const listName = filterList?.name || value.not_in_filter_list

      items.push(
        createConstraintItem(
          fieldLabel,
          `≠ ${listName}`,
          constraint.compliant,
          violations,
        ),
      )
    }
    if (value.equal_to !== null && value.equal_to !== undefined) {
      items.push(handleEqualToFilter(fieldLabel, value, constraint, violations))
    }
    if (value.not_equal_to !== null && value.not_equal_to !== undefined) {
      items.push(
        handleNotEqualToFilter(fieldLabel, value, constraint, violations),
      )
    }
    if (
      value.less_than_or_equal_to !== undefined ||
      value.greater_than_or_equal_to !== undefined ||
      value.less_than !== undefined ||
      value.greater_than !== undefined
    ) {
      items.push(
        ...handleRangeFilter(fieldLabel, value, constraint, violations),
      )
    }
  })

  // Add violations and violationsCount to each item
  return items.map((item) => ({
    ...item,
    violations: item.violations || violations,
    violationsCount: item.violationsCount || violationsCount,
  }))
}

function createSectorBarChart(
  sectorData: SectorWeight[],
  minValue: number,
  maxValue: number,
  isCompliant: boolean,
  isMarketValue = false,
): ConstraintItemData {
  return createConstraintItem(
    'Sector Allocation',
    <ChartContainer>
      <HorizontalTargetBarChart
        data={sectorData}
        yAxisKey="name"
        bars={[
          {
            key: 'value',
            fill: '#5A7B7D',
            radius: [4, 4, 4, 4],
            violationColor: '#F07070',
            isHighlighted: (entry: SectorWeight) =>
              entry.fullName === 'Technology',
          },
        ]}
        tooltipFormatter={(dataPoint: SectorWeight) => (
          <TooltipContainer>
            <TooltipLabel>{`Sector: ${toSentenceCase(dataPoint.fullName)}`}</TooltipLabel>
            <TooltipItemContainer>
              <TooltipColorIndicator style={{ backgroundColor: '#5A7B7D' }} />
              <TooltipItemText>
                {isMarketValue
                  ? `Value: ${formatMoney(dataPoint.value)}`
                  : `Weight: ${formatPercentage(dataPoint.value, { numDigits: 2 })}`}
              </TooltipItemText>
            </TooltipItemContainer>
          </TooltipContainer>
        )}
        barSize={16}
        minValue={isMarketValue ? minValue : minValue * 100}
        maxValue={isMarketValue ? maxValue : maxValue * 100}
        valueFormatter={
          isMarketValue
            ? (value) => formatMoney(value)
            : (value) => formatPercentage(value, { numDigits: 2 })
        }
      />
    </ChartContainer>,
    isCompliant,
  )
}

function calculateSectorWeights(
  fields: DiversificationConstraintValidation['fields'],
  useMarketValue = false,
): Record<string, number> {
  return (
    fields?.reduce(
      (acc, field) => {
        const value = useMarketValue ? field.market_value : field.weight
        if (field.field_value && value !== undefined) {
          const normalizedSector = field.field_value.trim()
          if (normalizedSector) {
            acc[normalizedSector] = (acc[normalizedSector] || 0) + value
          }
        }
        return acc
      },
      {} as Record<string, number>,
    ) || {}
  )
}

function createSectorData(
  sectorWeights: Record<string, number>,
  multiplyBy100: boolean = true,
): SectorWeight[] {
  return (
    Object.entries(sectorWeights)
      .filter(([, weight]) => weight > 0)
      .map(([sector, weight]) => {
        const MAX_LABEL_CHARS = 24
        const baseName =
          sector.length > 0
            ? // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
              sector[0]!.toUpperCase() + sector.slice(1).toLowerCase()
            : ''
        const formattedName =
          baseName.length > MAX_LABEL_CHARS
            ? `${baseName.slice(0, MAX_LABEL_CHARS)}..`
            : baseName

        return {
          name: formattedName,
          fullName: sector,
          value: multiplyBy100 ? decimalToPercentage(weight) : weight,
        }
      })
      // Sort by weight descending; if equal, fall back to alphabetical by name
      .sort((a, b) =>
        b.value !== a.value ? b.value - a.value : a.name.localeCompare(b.name),
      )
  )
}

function getDiversificationItems(
  constraint: DiversificationConstraintValidation,
  holdings?: Holding[],
  multiplyBy100: boolean = true,
  constraintInstanceType?: ConstraintInstanceType,
): ConstraintItemData[] {
  const items: ConstraintItemData[] = []
  const {
    field,
    max_weight_per_unique_value,
    min_weight_per_unique_value,
    max_market_value_per_unique_value,
    min_market_value_per_unique_value,
  } = constraint.constraint

  const fieldLabel = getFormattedFieldName(field, constraintInstanceType)
  // Calculate violations count from invalid_fields
  const violationsCount =
    constraint.fields?.filter((field) => !field.compliant).length || 0

  // Create violations details from invalid_fields
  const violations = constraint.fields
    ?.filter((field) => !field.compliant)
    ?.map((field) => {
      const holdingData = holdings?.find(
        (h) => h.isin === field.field_value || h.cusip === field.field_value,
      )
      return {
        isin: field.field_value,
        description: holdingData?.description || '',
        weight: field.weight,
        market_value: field.market_value,
      }
    })

  const isMarketValueConstraint =
    max_market_value_per_unique_value !== undefined ||
    min_market_value_per_unique_value !== undefined

  if (isMarketValueConstraint) {
    if (
      max_market_value_per_unique_value &&
      min_market_value_per_unique_value
    ) {
      items.push({
        ...createConstraintItem(
          fieldLabel,
          `${formatMoney(min_market_value_per_unique_value)} to ${formatMoney(max_market_value_per_unique_value)}`,
          constraint.compliant,
          violations,
          violationsCount,
        ),
        isMarketValueConstraint: true,
      })
    } else {
      if (max_market_value_per_unique_value) {
        items.push({
          ...createConstraintItem(
            `Max per ${fieldLabel}`,
            formatMoney(max_market_value_per_unique_value),
            constraint.compliant,
            violations,
            violationsCount,
          ),
          isMarketValueConstraint: true,
        })
      }

      if (min_market_value_per_unique_value) {
        items.push({
          ...createConstraintItem(
            `Min per ${fieldLabel}`,
            formatMoney(min_market_value_per_unique_value),
            constraint.compliant,
            violations,
            violationsCount,
          ),
          isMarketValueConstraint: true,
        })
      }
    }
  } else {
    // Handle weight percentage constraints
    if (max_weight_per_unique_value && min_weight_per_unique_value) {
      items.push(
        createConstraintItem(
          fieldLabel,
          `${formatPercentage(decimalToPercentage(min_weight_per_unique_value), { numDigits: 2 })} to ${formatPercentage(decimalToPercentage(max_weight_per_unique_value), { numDigits: 2 })}`,
          constraint.compliant,
          violations,
          violationsCount,
        ),
      )
    } else {
      // Handle individual min/max constraints if only one is available
      if (max_weight_per_unique_value) {
        items.push(
          createConstraintItem(
            `Max per ${fieldLabel}`,
            formatPercentage(decimalToPercentage(max_weight_per_unique_value), {
              numDigits: 2,
            }),
            constraint.compliant,
            violations,
            violationsCount,
          ),
        )
      }

      if (min_weight_per_unique_value) {
        items.push(
          createConstraintItem(
            `Min per ${fieldLabel}`,
            formatPercentage(decimalToPercentage(min_weight_per_unique_value), {
              numDigits: 2,
            }),
            constraint.compliant,
            violations,
            violationsCount,
          ),
        )
      }
    }
  }

  // Add sector allocation chart if applicable
  if (field === 'sector' && constraint.fields?.length) {
    const isMarketValueConstraint =
      min_market_value_per_unique_value !== undefined ||
      max_market_value_per_unique_value !== undefined

    const sectorWeights = calculateSectorWeights(
      constraint.fields,
      isMarketValueConstraint,
    )

    const sectorData = createSectorData(
      sectorWeights,
      isMarketValueConstraint ? false : multiplyBy100,
    )

    if (sectorData.length > 0) {
      const minValue = isMarketValueConstraint
        ? min_market_value_per_unique_value || 0
        : min_weight_per_unique_value || 0
      const maxValue = isMarketValueConstraint
        ? max_market_value_per_unique_value || 0
        : max_weight_per_unique_value || 0

      items.push(
        createSectorBarChart(
          sectorData,
          minValue,
          maxValue,
          constraint.compliant,
          isMarketValueConstraint,
        ),
      )
    }
  }

  return items
}

// Average value constraint helper functions
function createRatingNumberLine(
  constraint: AverageValueConstraintValidation,
  currentValue: SPRating | MoodysRating,
  targetValue?: SPRating | MoodysRating,
  minValue?: SPRating | MoodysRating,
  maxValue?: SPRating | MoodysRating,
  field?: string,
): ConstraintItemData {
  // Determine label based on field type
  const label =
    field === 'lowest_rating' ? 'Target Lowest Rating' : 'Target S&P Rating'

  return createConstraintItem(
    label,
    <TargetNumberLine
      current={currentValue ? { rating: currentValue } : undefined}
      target={targetValue ? { rating: targetValue } : undefined}
      min={minValue ? { rating: minValue } : undefined}
      max={maxValue ? { rating: maxValue } : undefined}
    />,
    constraint.compliant,
  )
}

function createMoodysRatingNumberLine(
  constraint: AverageValueConstraintValidation,
  currentValue: MoodysRating,
  targetValue?: MoodysRating,
  minValue?: MoodysRating,
  maxValue?: MoodysRating,
): ConstraintItemData {
  return createConstraintItem(
    "Target Moody's Rating",
    <TargetNumberLine
      current={{ rating: currentValue }}
      target={targetValue ? { rating: targetValue } : undefined}
      min={minValue ? { rating: minValue } : undefined}
      max={maxValue ? { rating: maxValue } : undefined}
    />,
    constraint.compliant,
  )
}

function createNumericNumberLine(
  constraint: AverageValueConstraintValidation,
  field: string,
  currentValue: number,
  targetValue: number,
  minValue?: number,
  maxValue?: number,
): ConstraintItemData {
  const isYearsField = ['maturity_in_years', 'convexity'].includes(field)

  const formatLabel = (value: number): string => {
    if (isYearsField) {
      return `${formatNumber(value, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })} ${value === 1 ? 'yr' : 'yrs'}`
    }
    if (field === 'duration') {
      return formatNumber(value, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    }
    return formatPercentage(value, { numDigits: 2 })
  }

  return createConstraintItem(
    `Target ${formatFieldLabel(field)}`,
    <TargetNumberLine
      target={{
        value: targetValue,
        label: formatLabel(targetValue),
      }}
      current={{
        value: currentValue,
        label: formatLabel(currentValue),
      }}
      min={
        minValue !== undefined
          ? {
              value: minValue,
              label: formatLabel(minValue),
            }
          : undefined
      }
      max={
        maxValue !== undefined
          ? {
              value: maxValue,
              label: formatLabel(maxValue),
            }
          : undefined
      }
      unit={isYearsField ? ValueUnit.Years : ValueUnit.Percentage}
    />,
    constraint.compliant,
  )
}

function getAverageValueItems(
  constraint: AverageValueConstraintValidation,
): ConstraintItemData[] {
  const items: ConstraintItemData[] = []
  const { field, min_average_value, target_average_value, max_average_value } =
    constraint.constraint
  const { holdings_average_value } = constraint

  if (isSpRatingField(field)) {
    items.push(
      createRatingNumberLine(
        constraint,
        holdings_average_value as SPRating,
        target_average_value as SPRating | undefined,
        min_average_value as SPRating | undefined,
        max_average_value as SPRating | undefined,
        field,
      ),
    )
    return items
  }

  if (isMoodysRatingField(field)) {
    items.push(
      createMoodysRatingNumberLine(
        constraint,
        holdings_average_value as MoodysRating,
        target_average_value as MoodysRating | undefined,
        min_average_value as MoodysRating | undefined,
        max_average_value as MoodysRating | undefined,
      ),
    )
    return items
  }

  if (target_average_value) {
    items.push(
      createNumericNumberLine(
        constraint,
        field,
        holdings_average_value as number,
        target_average_value as number,
        min_average_value as number | undefined,
        max_average_value as number | undefined,
      ),
    )
  }

  return items
}

interface FilterConditionField {
  equal_to?: string | number | null
  not_equal_to?: string | number | null
  less_than_or_equal_to?: string | number
  greater_than_or_equal_to?: string | number
  less_than?: string | number
  greater_than?: string | number
  in?: string[]
  not_in?: string[]
  starts_with?: string
  ends_with?: string
  contains?: string
  not_contains?: string
  in_filter_list?: string
  not_in_filter_list?: string
}

interface AllocationRange {
  min?: number
  max?: number
  target?: number
  current?: number
  type: 'weight' | 'market_value'
}

function createFilterDescription(
  filters: Record<string, FilterConditionField>,
  filterListsMap?: FilterListsMap,
  constraintInstanceType?: ConstraintInstanceType,
): string {
  const conditions: string[] = []

  Object.entries(filters).forEach(([key, value]) => {
    const fieldName = getFormattedFieldName(key, constraintInstanceType)
    if (value.greater_than !== undefined) {
      conditions.push(`${fieldName} > ${value.greater_than}`)
    }
    if (value.greater_than_or_equal_to !== undefined) {
      conditions.push(`${fieldName} >= ${value.greater_than_or_equal_to}`)
    }
    if (value.less_than !== undefined) {
      conditions.push(`${fieldName} < ${value.less_than}`)
    }
    if (value.less_than_or_equal_to !== undefined) {
      conditions.push(`${fieldName} <= ${value.less_than_or_equal_to}`)
    }
    if (value.equal_to !== undefined) {
      conditions.push(`${fieldName} = ${value.equal_to}`)
    }
    if (value.not_equal_to !== undefined) {
      conditions.push(`${fieldName} != ${value.not_equal_to}`)
    }
    if (value.in !== undefined) {
      conditions.push(`${fieldName} IN ${value.in}`)
    }
    if (value.not_in !== undefined) {
      conditions.push(`${fieldName} NOT IN ${value.not_in}`)
    }
    if (value.starts_with !== undefined) {
      conditions.push(`${fieldName} STARTS WITH ${value.starts_with}`)
    }
    if (value.ends_with !== undefined) {
      conditions.push(`${fieldName} ENDS WITH ${value.ends_with}`)
    }
    if (value.contains !== undefined) {
      conditions.push(`${fieldName} CONTAINS ${value.contains}`)
    }
    if (value.not_contains !== undefined) {
      conditions.push(`${fieldName} NOT CONTAINS ${value.not_contains}`)
    }
    if (value.in_filter_list !== undefined) {
      const filterList = filterListsMap?.[value.in_filter_list]
      const listName = filterList?.name || value.in_filter_list
      conditions.push(`${fieldName} IN ${listName}`)
    }
    if (value.not_in_filter_list !== undefined) {
      const filterList = filterListsMap?.[value.not_in_filter_list]
      const listName = filterList?.name || value.not_in_filter_list
      conditions.push(`${fieldName} NOT IN ${listName}`)
    }
  })

  return conditions.join(' AND ')
}

function formatValue(value: number | undefined, isWeight: boolean): string {
  if (value === undefined) {
    return '—'
  }

  if (isWeight) {
    return formatPercentage(decimalToPercentage(value), { numDigits: 2 })
  }
  return formatMoney(value)
}

function createRangeItem(
  range: AllocationRange,
  constraint: AllocationConstraintValidation,
  filterDescription: string,
): ConstraintItemData[] {
  const isWeight = range.type === 'weight'

  // Helper function to format value if defined
  const formatRangeValue = (value?: number) =>
    value !== undefined
      ? {
          value: decimalToPercentage(value),
          label: formatValue(value, isWeight),
        }
      : undefined

  return [
    createConstraintItem(
      filterDescription,
      <TargetNumberLine
        current={formatRangeValue(range.current)}
        target={formatRangeValue(range.target)}
        min={formatRangeValue(range.min)}
        max={formatRangeValue(range.max)}
      />,
      constraint.compliant,
    ),
  ]
}

function getAllocationItems(
  constraint: AllocationConstraintValidation,
  filterListsMap?: FilterListsMap,
  constraintInstanceType?: ConstraintInstanceType,
): ConstraintItemData[] {
  const items: ConstraintItemData[] = []
  const {
    filter,
    min_weight,
    target_weight,
    max_weight,
    min_market_value,
    target_market_value,
    max_market_value,
  } = constraint.constraint

  // Create filter description
  const filters = filter
  const filterDesc = createFilterDescription(
    filters as Record<string, FilterConditionField>,
    filterListsMap,
    constraintInstanceType,
  )
  const filterDescription = filterDesc ? filterDesc : 'Allocation constraint'

  // Add market value range items only if any market value constraints are defined
  const hasMarketValueConstraints =
    !isNil(min_market_value) ||
    !isNil(max_market_value) ||
    !isNil(target_market_value)

  if (hasMarketValueConstraints) {
    const marketValueRange: AllocationRange = {
      min: min_market_value,
      max: max_market_value,
      target: target_market_value,
      current: constraint.market_value,
      type: 'market_value',
    }
    items.push(
      ...createRangeItem(marketValueRange, constraint, filterDescription),
    )
  } else {
    // Add weight range items
    const weightRange: AllocationRange = {
      min: min_weight,
      max: max_weight,
      target: target_weight,
      current: constraint.weight,
      type: 'weight',
    }
    items.push(...createRangeItem(weightRange, constraint, filterDescription))
  }

  return items
}

function getCashflowBandItems(
  constraint: CashflowBandConstraintValidation,
): ConstraintItemData[] {
  return [
    {
      label: 'Level cashflows',
      value: renderCashflowBandRange(constraint.constraint),
      status: getComplianceStatus(isCompliant(constraint) === true),
    },
  ]
}

const EQUAL_WEIGHT_DEVIATION_LABELS: Record<EqualWeightDeviation, string> = {
  too_small: 'Too small',
  too_large: 'Too large',
}

const formatTargetWeight = (weight: number): string =>
  formatPercentage(decimalToPercentage(weight), { numDigits: 2 })

// A tier child's verdict sits under its tiered rule, so the caller resolves its piece to name the tier.
export function getEqualWeightItems({
  validation,
  piece,
}: {
  validation: EqualWeightConstraintValidation
  piece: Piece | undefined
}): ConstraintItemData[] {
  const target = validation.equal_weight_target
  const violations = (validation.fields ?? [])
    .filter((field) => !field.compliant)
    .map((field) => ({
      isin: field.field_value,
      description: '',
      weight: field.weight,
      note: field.equal_weight_deviation
        ? EQUAL_WEIGHT_DEVIATION_LABELS[field.equal_weight_deviation]
        : undefined,
    }))
  return [
    createConstraintItem(
      renderEqualWeightLabel({ constraint: validation.constraint, piece }),
      target
        ? `${formatTargetWeight(target.min_weight)} to ${formatTargetWeight(target.max_weight)} each`
        : renderEqualWeightTolerance(
            validation.constraint.equal_weight_tolerance,
          ),
      isCompliant(validation) === true,
      violations,
      violations.length,
    ),
  ]
}

export const getConstraintItems = (
  constraint: ModelConstraintValidation,
  holdings?: Holding[],
  multiplyBy100: boolean = true,
  filterListsMap?: FilterListsMap,
  constraintInstanceType?: ConstraintInstanceType,
): ConstraintItemData[] => {
  // An equal-weight rule has no per-value limits, so it skips the diversification case.
  if (isEqualWeightValidation(constraint))
    return getEqualWeightItems({ validation: constraint, piece: undefined })
  switch (constraint.constraint.type) {
    case ModelConstraintType.LadderPolicy:
      return getLadderPolicyItems(
        constraint as LadderPolicyConstraintValidation,
        holdings,
      )
    case ModelConstraintType.Universe:
    case ModelConstraintType.BuyUniverse:
      return getUniverseItems(
        constraint as UniverseConstraintValidation,
        holdings,
        filterListsMap,
        constraintInstanceType,
      )
    case ModelConstraintType.Diversification:
      return getDiversificationItems(
        constraint as DiversificationConstraintValidation,
        holdings,
        multiplyBy100,
        constraintInstanceType,
      )
    case ModelConstraintType.AverageValue:
      return getAverageValueItems(
        constraint as AverageValueConstraintValidation,
      )
    case ModelConstraintType.Allocation:
      return getAllocationItems(
        constraint as AllocationConstraintValidation,
        filterListsMap,
        constraintInstanceType,
      )
    case ModelConstraintType.CashflowBand:
      return getCashflowBandItems(
        constraint as CashflowBandConstraintValidation,
      )
    default:
      return []
  }
}
